import React, { useState, useContext, useEffect } from "react"
import { useNavigate } from "react-router-dom"
import { styled, createTheme, ThemeProvider } from "@mui/material/styles"
import {
  Box,
  Typography,
  Chip,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
} from "@mui/material"
import Swal from "sweetalert2"
import dayjs from "dayjs"

// Layouts y Componentes Base
import Header from "../../layouts/Header"
import BackIcon from "../../components/BackIcon"
import CustomConditionalActionsTableServer from "../../components/CustomConditionalActionsTableServerSide"
import CustomFieldsetAccordion from "../../components/CustomFieldsetAccordion"

// Contexto y Utilidades
import { GlobalContext } from "../../contexts/GlobalContext"
import { useMutation, api } from "../../api"
import fetchwrapper from "../../services/interceptors/fetchwrapper"
import getIconComponent from "../utils/getIconComponent"
import { handleAllExportDataCSV } from "../utils/reactTableActions/exportToolbarActions"

import { Search as SearchIcon, History as HistoryIcon, FormatListBulleted as ListIcon } from "@mui/icons-material"

// =================================================================
// ESTILOS Y TEMA (Estándar SIAC FUTURESOFT)
// =================================================================
const StyledRoot = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  marginTop: theme.spacing(3),
  flexGrow: 1,
  padding: "0 16px",
  minHeight: "80vh",
}))

const theme = createTheme({
  palette: {
    primary: { main: "#196C87" },
    secondary: { main: "#2E7D32" },
    error: { main: "#d32f2f" },
    warning: { main: "#ed6c02" },
  },
})

const ConsultaDeCartera = () => {
  const navigate = useNavigate()
  const { selectedMenuInfo } = useContext(GlobalContext)

  // =================================================================
  // ESTADOS DE FILTROS Y MODAL
  // =================================================================
  const hoy = dayjs().format("YYYY-MM-DD")
  const primerDiaAnio = dayjs().startOf("year").format("YYYY-MM-DD")

  const [filtros, setFiltros] = useState({
    fechaDesde: primerDiaAnio,
    fechaHasta: hoy,
    clicodigo: "",
    alcance: "CIA",
    soloLegal: false,
  })

  const [filtrosAplicados, setFiltrosAplicados] = useState({ ...filtros })
  const [expanded, setExpanded] = useState(true)
  const [refreshKey, setRefreshKey] = useState(0) // Fuerza recarga de la tabla

  // Estados del Modal Historial (Drill-Down)
  const [showDetailsModal, setShowDetailsModal] = useState(false)
  const [facturaSeleccionada, setFacturaSeleccionada] = useState(null)
  const [historialGestiones, setHistorialGestiones] = useState([])
  const [isExporting, setIsExporting] = useState(false)

  // =================================================================
  // MUTATIONS (Conexión al Backend)
  // =================================================================
  const { mutate: fetchDetalleCartera, isPending: isLoadingDetalle } = useMutation({
    fn: async (facnumfac) => {
      const response = await api.post("/ConsultadeCartera/getDetalleCartera", { facnumfac })
      return response
    },
    showError: "modal",
    showSuccess: "silent",
    onSuccess: (res) => {
      const payload = res && res.data && typeof res.data === "object" && "success" in res.data ? res.data : res
      if (payload?.success) {
        setHistorialGestiones(payload.data.historialGestiones || [])
      }
    },
  })

  // =================================================================
  // MANEJADORES
  // =================================================================
  const handleToggle = () => setExpanded((prev) => !prev)

  const handleFiltroChange = (e) => {
    const { name, value, type, checked } = e.target
    setFiltros((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }))
  }

  const aplicarFiltros = () => {
    setFiltrosAplicados({ ...filtros })
    setRefreshKey((prev) => prev + 1) // Dispara re-render de la tabla Custom
  }

  const resetFiltros = () => {
    const defaultFiltros = {
      fechaDesde: primerDiaAnio,
      fechaHasta: hoy,
      clicodigo: "",
      alcance: "CIA",
      soloLegal: false,
    }
    setFiltros(defaultFiltros)
    setFiltrosAplicados(defaultFiltros)
    setRefreshKey((prev) => prev + 1)
  }

  const handleVerHistorial = (rowOriginal) => {
    setFacturaSeleccionada(rowOriginal)
    setHistorialGestiones([])
    setShowDetailsModal(true)
    fetchDetalleCartera(rowOriginal.facnumfac)
  }

  // Llama al backend en Python para el PDF Estructurado
  const handleExportPDFBackend = async () => {
    setIsExporting(true)
    try {
      const response = await fetchwrapper("/ConsultadeCartera/exportCarteraPdf", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(filtrosAplicados),
      })

      const blob = await response.blob()
      if (blob.type === "application/json") {
        const text = await blob.text()
        const json = JSON.parse(text)
        Swal.fire("Aviso", json.message || "No se pudo generar el reporte", "warning")
        return
      }

      const url = window.URL.createObjectURL(blob)
      const a = document.createElement("a")
      a.href = url
      a.download = `Reporte_Gestion_Cartera_${dayjs().format("YYYYMMDD")}.pdf`
      document.body.appendChild(a)
      a.click()
      a.remove()
      window.URL.revokeObjectURL(url)
    } catch (error) {
      console.error(error)
      Swal.fire("Error", "Ocurrió un error al exportar el documento.", "error")
    } finally {
      setIsExporting(false)
    }
  }

  return (
    <ThemeProvider theme={theme}>
      <Header />
      <div className="main main-app p-3 p-lg-4" style={{ backgroundColor: "#f4f6f8", minHeight: "100vh" }}>
        <BackIcon />

        <Box display="flex" justifyContent="center" alignItems="center" mb={4}>
          <Typography variant="h4" fontWeight="bold" color="primary.main">
            Consulta y Exportación de Cartera
          </Typography>
        </Box>

        <StyledRoot>
          {/* ======================= ACORDEÓN DE FILTROS ======================= */}
          <CustomFieldsetAccordion title="Filtros Analíticos" expanded={expanded} onToggle={handleToggle}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={2}>
                <TextField
                  fullWidth
                  type="date"
                  label="Fecha Emisión (Desde)"
                  name="fechaDesde"
                  size="small"
                  InputLabelProps={{ shrink: true }}
                  value={filtros.fechaDesde}
                  onChange={handleFiltroChange}
                />
              </Grid>
              <Grid item xs={12} md={2}>
                <TextField
                  fullWidth
                  type="date"
                  label="Fecha Emisión (Hasta)"
                  name="fechaHasta"
                  size="small"
                  InputLabelProps={{ shrink: true }}
                  value={filtros.fechaHasta}
                  onChange={handleFiltroChange}
                />
              </Grid>
              <Grid item xs={12} md={2}>
                <TextField
                  fullWidth
                  label="Cód. Cliente (Opcional)"
                  name="clicodigo"
                  size="small"
                  value={filtros.clicodigo}
                  onChange={handleFiltroChange}
                  placeholder="Ej: 000123"
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <FormControl size="small" fullWidth>
                  <InputLabel>Alcance de Búsqueda</InputLabel>
                  <Select
                    name="alcance"
                    value={filtros.alcance}
                    onChange={handleFiltroChange}
                    label="Alcance de Búsqueda"
                  >
                    <MenuItem value="LOC">Solo esta Localidad</MenuItem>
                    <MenuItem value="CIA">Toda esta Compañía</MenuItem>
                    <MenuItem value="ALL">Consolidado (Todas las CIAs)</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={1}>
                <FormControlLabel
                  control={
                    <Switch checked={filtros.soloLegal} onChange={handleFiltroChange} name="soloLegal" color="error" />
                  }
                  label={
                    <Typography variant="caption" fontWeight="bold">
                      Legal
                    </Typography>
                  }
                />
              </Grid>
              <Grid item xs={12} md={2} display="flex" gap={1} justifyContent="flex-end">
                <Button variant="outlined" color="secondary" onClick={resetFiltros}>
                  Limpiar
                </Button>
                <Button variant="contained" onClick={aplicarFiltros} startIcon={<SearchIcon />}>
                  Consultar
                </Button>
              </Grid>
            </Grid>
          </CustomFieldsetAccordion>

          <Box mt={2}>
            {/* ======================= TABLA PERSONALIZADA ESTÁNDAR ======================= */}
            <CustomConditionalActionsTableServer
              key={refreshKey} // Fuerza el re-render al consultar
              endpoint="/ConsultadeCartera/getConsultaCartera"
              errorMsgFilterSearch="Error al cargar la información de cartera"
              queryKeyModal={`ConsultaCarteraData_${refreshKey}`}
              perPage={15}
              rowActionsWidthTable={140}
              enableColumnFilters={true}
              // Inyectamos los filtros como payload al backend
              extraParams={filtrosAplicados}
              bodyParams={filtrosAplicados}
              additionalPayload={filtrosAplicados}
              // ACCIONES DE FILA (Row Actions)
              rowActions={(row) => {
                const actions = []
                // Si tiene el permiso "BUSCAR" o "VER" le mostramos el Historial
                actions.push({
                  label: "Ver Historial",
                  key: "HISTORIAL",
                  icon: getIconComponent("History", "material"),
                  onClick: (row) => handleVerHistorial(row.original),
                })
                return actions
              }}
              // ACCIONES SUPERIORES (Top Toolbar)
              topToolbarCustomActions={({ table, device }) => {
                const exportarAction = selectedMenuInfo?.data?.barraAcciones?.find((a) => a.acccaption === "EXPORTAR")
                const toolbarActions = []

                if (exportarAction) {
                  toolbarActions.push({
                    type: "dropdown",
                    label: isExporting ? "GENERANDO PDF..." : "EXPORTAR DATOS",
                    key: "EXPORTAR",
                    icon: isExporting ? (
                      <CircularProgress size={20} />
                    ) : (
                      getIconComponent(exportarAction.accnameicono, exportarAction.acctipoico)
                    ),
                    actions: [
                      {
                        label: "Descargar PDF Estructurado",
                        key: "exportarPDF",
                        icon: getIconComponent("PictureAsPdf", "material"),
                        onClick: () => handleExportPDFBackend(), // Llama a Python
                      },
                      {
                        label: "Descargar Excel (CSV)",
                        key: "exportarCSV",
                        icon: getIconComponent("TableView", "material"),
                        // El CSV usa la data actual de la tabla en pantalla
                        onClick: ({ data }) => handleAllExportDataCSV(data, `Cartera_${new Date().getTime()}`),
                      },
                    ],
                  })
                }
                return toolbarActions
              }}
              // COLUMNAS
              columnsTable={[
                {
                  accessorKey: "clicodigo",
                  header: "Cód. Cliente",
                  size: 100,
                  enableColumnFilter: true,
                },
                {
                  accessorKey: "clinombre",
                  header: "Nombre del Cliente",
                  size: 250,
                  enableColumnFilter: true,
                  Cell: ({ cell }) => <Typography fontWeight="500">{cell.getValue()}</Typography>,
                },
                {
                  accessorKey: "facnumfac",
                  header: "Nº Factura / Doc",
                  size: 150,
                  enableColumnFilter: true,
                  Cell: ({ cell }) => (
                    <Typography fontWeight="bold" color="primary.main">
                      {cell.getValue()}
                    </Typography>
                  ),
                },
                {
                  accessorKey: "facfecemi",
                  header: "Fec. Emisión",
                  size: 110,
                },
                {
                  accessorKey: "facfecven",
                  header: "Fec. Vencimiento",
                  size: 120,
                },
                {
                  accessorKey: "factotal",
                  header: "Total Doc.",
                  size: 110,
                  Cell: ({ cell }) => {
                    const val = Number(cell.getValue())
                    return <span>${!isNaN(val) ? val.toFixed(2) : "0.00"}</span>
                  },
                },
                {
                  accessorKey: "facsaldo",
                  header: "Saldo",
                  size: 110,
                  Cell: ({ cell }) => {
                    const val = Number(cell.getValue())
                    const isDeuda = val > 0
                    return (
                      <Typography fontWeight="bold" color={isDeuda ? "error.main" : "text.secondary"}>
                        ${!isNaN(val) ? val.toFixed(2) : "0.00"}
                      </Typography>
                    )
                  },
                },
                {
                  accessorKey: "gesaccion",
                  header: "Última Acción",
                  size: 160,
                  filterVariant: "select",
                  filterSelectOptions: ["COBRANZA", "CALL CENTER", "RECAUDADOR", "SIN GESTION"],
                  Cell: ({ cell }) => {
                    const value = cell.getValue()
                    return value ? <Chip label={value} color="info" size="small" variant="outlined" /> : <span>-</span>
                  },
                },
                {
                  accessorKey: "gescomentario",
                  header: "Comentario Cobranza",
                  size: 300,
                },
                {
                  accessorKey: "facflaglegal",
                  header: "Estado Legal",
                  size: 120,
                  Cell: ({ cell }) => {
                    return cell.getValue() !== 0 ? <Chip label="LEGAL" size="small" color="error" /> : <span>-</span>
                  },
                },
              ]}
            />
          </Box>
        </StyledRoot>

        {/* ======================= MODAL DE HISTORIAL DE COBRANZA ======================= */}
        <Dialog open={showDetailsModal} onClose={() => setShowDetailsModal(false)} maxWidth="lg" fullWidth>
          <DialogTitle sx={{ bgcolor: "#196C87", color: "#fff", display: "flex", alignItems: "center", gap: 1 }}>
            <HistoryIcon />
            Historial de Gestiones - Factura: {facturaSeleccionada?.facnumfac}
          </DialogTitle>
          <DialogContent sx={{ mt: 2 }}>
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={12} md={6}>
                <Typography variant="body2" color="textSecondary">
                  Cliente:
                </Typography>
                <Typography variant="subtitle1" fontWeight="bold">
                  {facturaSeleccionada?.clicodigo} - {facturaSeleccionada?.clinombre}
                </Typography>
              </Grid>
              <Grid item xs={12} md={3}>
                <Typography variant="body2" color="textSecondary">
                  Emisión / Vence:
                </Typography>
                <Typography variant="subtitle2">
                  {facturaSeleccionada?.facfecemi} / {facturaSeleccionada?.facfecven}
                </Typography>
              </Grid>
              <Grid item xs={12} md={3}>
                <Typography variant="body2" color="textSecondary">
                  Saldo Pendiente:
                </Typography>
                <Typography variant="h6" fontWeight="bold" color="error">
                  ${facturaSeleccionada?.facsaldo ? Number(facturaSeleccionada.facsaldo).toFixed(2) : "0.00"}
                </Typography>
              </Grid>
            </Grid>

            <Divider sx={{ mb: 2 }} />

            <Typography
              variant="subtitle2"
              color="primary"
              sx={{ mb: 1, display: "flex", alignItems: "center", gap: 1 }}
            >
              <ListIcon fontSize="small" /> Bitácora de Seguimiento
            </Typography>

            {isLoadingDetalle ? (
              <Box display="flex" justifyContent="center" p={4}>
                <CircularProgress />
              </Box>
            ) : (
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead sx={{ bgcolor: "#f5f5f5" }}>
                    <TableRow>
                      <TableCell>
                        <b>Fecha</b>
                      </TableCell>
                      <TableCell>
                        <b>Hora</b>
                      </TableCell>
                      <TableCell>
                        <b>Usuario</b>
                      </TableCell>
                      <TableCell>
                        <b>Acción</b>
                      </TableCell>
                      <TableCell>
                        <b>Comentario</b>
                      </TableCell>
                      <TableCell>
                        <b>Compromiso</b>
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {historialGestiones.length > 0 ? (
                      historialGestiones.map((row, idx) => (
                        <TableRow key={idx} hover>
                          <TableCell>{row.gesfecisys}</TableCell>
                          <TableCell>{row.geshorisys}</TableCell>
                          <TableCell>{row.gesusuisys}</TableCell>
                          <TableCell>
                            <Chip label={row.gesaccion || "N/A"} size="small" color="primary" variant="outlined" />
                          </TableCell>
                          <TableCell>{row.gescomentario}</TableCell>
                          <TableCell>{row.gescompromiso || "-"}</TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={6} align="center">
                          No hay gestiones registradas.
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setShowDetailsModal(false)} variant="outlined" color="primary">
              Cerrar
            </Button>
          </DialogActions>
        </Dialog>
      </div>
    </ThemeProvider>
  )
}

export default ConsultaDeCartera
